import * as React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { Check, ChevronDown, Search, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MAX_VISIBLES, filtrerOptions } from '@/components/ui/searchable-select';

/**
 * Un menu à choix multiple qu'on peut fouiller : le pendant de
 * `SearchableSelect` quand on choisit plusieurs entités (les camions d'un
 * décompte, par exemple).
 *
 * Même recherche (accents, casse, espaces et ponctuation ignorés, mots dans
 * n'importe quel ordre), même plafond de `MAX_VISIBLES` lignes montées. Chaque
 * ligne porte une case ; la liste reste ouverte pendant qu'on coche.
 * « Tout choisir » et « Tout retirer » agissent sur tous les résultats de la
 * recherche (y compris ceux au-delà du plafond), jamais sur ce qu'on ne voit pas.
 *
 * Les choix apparaissent en pastilles retirables à côté du déclencheur (les
 * `maxPastilles` premières, puis « +N »).
 *
 * `onValueChange(suivants, changement)` reçoit la nouvelle liste de valeurs et
 * ce qui vient de changer : `{ values, selected }` (valeurs cochées si
 * `selected`, décochées sinon). L'appelant qui tient déjà son propre état
 * (cases d'un tableau) n'applique que le changement.
 *
 * Les options : `{ value, label, description?, keywords?, disabled? }`.
 */
const SearchableMultiSelect = ({
  value = [],
  onValueChange,
  options = [],
  placeholder = 'Choisir…',
  searchPlaceholder = 'Rechercher…',
  emptyMessage = 'Aucun résultat',
  unite = 'choisi',
  disabled = false,
  pastilles = true,
  maxPastilles = 6,
  id,
  className,
  contentClassName,
  'aria-label': ariaLabel,
  'data-testid': testId,
}) => {
  const [ouvert, setOuvert] = React.useState(false);
  const [recherche, setRecherche] = React.useState('');
  const [surligne, setSurligne] = React.useState('');
  const saisieRef = React.useRef(null);
  const panneauId = React.useId();

  const choisies = React.useMemo(() => new Set((value || []).map(String)), [value]);
  const entrees = React.useMemo(
    () => options.map((option, position) => ({ ...option, cle: `option-${position}` })),
    [options],
  );
  const resultats = React.useMemo(() => filtrerOptions(entrees, recherche), [entrees, recherche]);
  const visibles = resultats.slice(0, MAX_VISIBLES);
  const masques = resultats.length - visibles.length;
  const actifs = resultats.filter((option) => !option.disabled);
  const nombre = entrees.filter((option) => choisies.has(String(option.value))).length;

  React.useEffect(() => {
    if (!ouvert) return;
    const premier = visibles.find((option) => !option.disabled);
    setSurligne(premier ? premier.cle : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert, recherche, entrees]);

  const changerOuverture = (suivant) => {
    setOuvert(suivant);
    if (!suivant) setRecherche('');
  };

  const appliquer = (valeurs, selected) => {
    if (valeurs.length === 0) return;
    const cles = new Set(valeurs.map(String));
    const suivants = selected
      ? [...(value || []), ...valeurs.filter((valeur) => !choisies.has(String(valeur)))]
      : (value || []).filter((valeur) => !cles.has(String(valeur)));
    onValueChange?.(suivants, { values: valeurs, selected });
  };

  const basculer = (option) => {
    if (option.disabled || disabled) return;
    appliquer([option.value], !choisies.has(String(option.value)));
  };

  const toutChoisir = () =>
    appliquer(
      actifs.filter((option) => !choisies.has(String(option.value))).map((option) => option.value),
      true,
    );
  const toutRetirer = () =>
    appliquer(
      actifs.filter((option) => choisies.has(String(option.value))).map((option) => option.value),
      false,
    );

  const aChoisir = actifs.some((option) => !choisies.has(String(option.value)));
  const aRetirer = actifs.some((option) => choisies.has(String(option.value)));
  const enPastilles = entrees.filter((option) => choisies.has(String(option.value)));
  const pastillesVisibles = enPastilles.slice(0, maxPastilles);
  const pastillesMasquees = enPastilles.length - pastillesVisibles.length;
  const resume = nombre === 0 ? placeholder : `${nombre} ${unite}${nombre > 1 ? 's' : ''}`;
  const sousTestId = (suffixe) => (testId ? `${testId}-${suffixe}` : undefined);

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      <Popover open={ouvert} onOpenChange={changerOuverture} modal>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            role="combobox"
            aria-expanded={ouvert}
            aria-controls={ouvert ? panneauId : undefined}
            aria-haspopup="listbox"
            aria-label={ariaLabel}
            data-testid={testId}
            disabled={disabled}
            className="flex h-8 min-w-[11rem] items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-transparent px-3 text-left text-xs shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className={cn('min-w-0 flex-1 truncate', nombre === 0 && 'text-muted-foreground')}>{resume}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={cn('w-80 p-0', contentClassName)}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            saisieRef.current?.focus();
          }}
        >
          <CommandPrimitive
            id={panneauId}
            shouldFilter={false}
            loop
            value={surligne}
            onValueChange={setSurligne}
            label={ariaLabel || placeholder}
          >
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
              <CommandPrimitive.Input
                ref={saisieRef}
                value={recherche}
                onValueChange={setRecherche}
                placeholder={searchPlaceholder}
                className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            <div className="flex items-center justify-between gap-2 border-b px-2 py-1.5">
              <span className="text-xs tabular-nums text-muted-foreground" data-testid={sousTestId('compteur')}>
                {nombre} sur {entrees.length}
              </span>
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={toutChoisir}
                  disabled={disabled || !aChoisir}
                  className="rounded px-2 py-1 text-xs font-medium text-primary hover:bg-accent disabled:pointer-events-none disabled:opacity-40"
                  data-testid={sousTestId('tout-choisir')}
                >
                  Tout choisir
                </button>
                <button
                  type="button"
                  onClick={toutRetirer}
                  disabled={disabled || !aRetirer}
                  className="rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-accent disabled:pointer-events-none disabled:opacity-40"
                  data-testid={sousTestId('tout-retirer')}
                >
                  Tout retirer
                </button>
              </span>
            </div>
            <CommandPrimitive.List
              className="overflow-y-auto overflow-x-hidden p-1"
              style={{
                maxHeight: 'min(18rem, calc(var(--radix-popover-content-available-height, 21rem) - 6rem))',
              }}
            >
              {visibles.length === 0 ? (
                <p className="px-2 py-6 text-center text-sm text-muted-foreground">{emptyMessage}</p>
              ) : null}
              {visibles.map((option) => {
                const coche = choisies.has(String(option.value));
                return (
                  <CommandPrimitive.Item
                    key={option.cle}
                    value={option.cle}
                    disabled={option.disabled || disabled}
                    onSelect={() => basculer(option)}
                    aria-checked={coche}
                    data-checked={coche ? 'true' : 'false'}
                    data-testid={sousTestId(`option-${option.value}`)}
                    className="relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50"
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary',
                        coche && 'bg-primary text-primary-foreground',
                      )}
                    >
                      {coche ? <Check className="h-3.5 w-3.5" /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option.label}</span>
                      {option.description ? (
                        <span className="block truncate text-xs text-muted-foreground">{option.description}</span>
                      ) : null}
                    </span>
                  </CommandPrimitive.Item>
                );
              })}
              {masques > 0 ? (
                <p className="px-2 py-2 text-xs text-muted-foreground">
                  {masques} autre{masques > 1 ? 's' : ''} résultat{masques > 1 ? 's' : ''} : précisez la recherche.
                </p>
              ) : null}
            </CommandPrimitive.List>
          </CommandPrimitive>
        </PopoverContent>
      </Popover>

      {pastilles
        ? pastillesVisibles.map((option) => (
            <span
              key={option.cle}
              className="inline-flex max-w-[10rem] items-center gap-1 rounded-md border border-info/25 bg-info/10 py-0.5 pl-2 pr-1 text-xs font-semibold text-info"
              data-testid={sousTestId(`pastille-${option.value}`)}
            >
              <span className="truncate">{option.label}</span>
              <button
                type="button"
                onClick={() => basculer(option)}
                disabled={disabled || option.disabled}
                aria-label={`Retirer ${typeof option.label === 'string' ? option.label : ''}`.trim()}
                className="rounded-sm p-0.5 hover:bg-info/20 disabled:pointer-events-none disabled:opacity-50"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          ))
        : null}
      {pastilles && pastillesMasquees > 0 ? (
        <span className="text-xs font-semibold text-muted-foreground" data-testid={sousTestId('pastilles-plus')}>
          +{pastillesMasquees}
        </span>
      ) : null}
    </div>
  );
};

SearchableMultiSelect.displayName = 'SearchableMultiSelect';

export { SearchableMultiSelect };
export default SearchableMultiSelect;
